<template>
  <div>
    <div v-if="files.length === 0 || multiple"
         @dragenter.prevent="onDragEnter" @dragover.prevent @dragleave.prevent="onDragLeave" @drop.prevent="onDrop" @click.prevent.stop="handleSelect"
         class="c-file-upload" :class="{ 'c-file-upload-drag-active': dragActive, 'c-file-upload-unsupported-file': error }">

      <CPaste :onPaste="onInput" />
      <p v-if="error">{{ error }}</p>

      <template v-else>
        <CIcon name="attachment-paperclip" :size="32" class="c-file-upload-icon"/>
        <div class="u-ml-2">
          <p class="c-file-upload-description">{{ $t('fileUpload.description') }}</p>
          <p>{{ acceptLabel }}</p>
        </div>
      </template>
    </div>

    <CButton v-for="file in files" @click="remove(file)" class="c-file-upload-selection">
      <CShelf>
        <template #left>
          <CIcon name="document"/>
          <p>{{ file.name }}</p>
        </template>
        <template #right>
          <CIcon name="delete-trash" class="c-file-upload-selection-remove"/>
        </template>
      </CShelf>
    </CButton>
  </div>
</template>

<script setup lang="ts">
  import { without } from 'lodash';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import FileCategory from '../../constants/FileCategory';
  import { delay } from '../../utils';
  import { ACCEPT_ALL,
           ACCEPT_IMAGES,
           ACCEPT_MEDIA,
           ACCEPT_PDF,
           checkFileType, getAllowedFilesDescription,
           selectFiles } from '../../utils/selectFiles';
  import { present } from '../../utils/typescript';
  import CIcon from '../CIcon.vue';
  import CPaste from '../CPaste.vue';
  import CShelf from '../CShelf.vue';
  import CButton from '../buttons/CButton.vue';

  type ValueType = File | null | Array<File>;

  const MAX_UPLOAD_SIZE_MB = 300;
  const MAX_UPLOAD_SIZE = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

  // putting i18n translation here won't work, so we just set the key
  const WRONG_TYPE_KEY = 'fileUpload.unsupported';
  const TOO_BIG_KEY = 'fileUpload.tooBig';

  const props = defineProps<{
    accept?: string;
    category?: FileCategory;
    multiple?: boolean;
  }>();

  const model = defineModel<ValueType>();

  const i18n = useI18n();
  let dragCount = 0;
  const dragActive = ref(false);
  const error = ref('');

  const files = computed<Array<File>>(() => {
    if (!model.value) return [];
    if (Array.isArray(model.value)) return model.value;
    return [model.value];
  });

  const accept = computed(() => {
    switch (props.category) {
      case FileCategory.IMAGE:
        return ACCEPT_IMAGES;
      case FileCategory.MEDIA:
        return ACCEPT_MEDIA;
      case FileCategory.DOCUMENT:
        return ACCEPT_PDF;
      case FileCategory.ALL:
        return ACCEPT_ALL;
      default:
        return present(props.accept);
    }
  });

  const acceptLabel = computed(() => {
    const params = { allowedFiles: getAllowedFilesDescription(accept.value), maxMb: MAX_UPLOAD_SIZE_MB };
    return i18n.t('fileUpload.allowedFiles', params);
  });

  function onDragEnter(e: DragEvent) {
    if (dragCount++ === 0) {
      if (isAccepted(e)) {
        dragActive.value = true;
      } else {
        showError(WRONG_TYPE_KEY);
      }
    }
  }

  function onDragLeave() {
    if (--dragCount < 1) {
      dragActive.value = false;
      dragCount = 0;
    }
  }

  function onDrop(e: DragEvent) {
    dragCount = 0;
    onDragLeave();
    if (!isAccepted(e)) return showError(WRONG_TYPE_KEY);
    onInput(Array.from(e.dataTransfer?.files ?? []));
  }

  function isAccepted(e: DragEvent) {
    const item = e.dataTransfer?.items[0];
    if (!accept.value || !item) return true;
    return item.kind === 'file' && checkFileType(item.type, accept.value);
  }

  async function showError(key: string): Promise<void> {
    if (error.value) return;
    error.value = i18n.t(key);
    await delay(2000);
    error.value = '';
  }

  function handleSelect() {
    selectFiles(onInput, accept.value, props.multiple);
  }

  async function onInput(newFiles: Array<File>) {
    const allFiles = props.multiple ? [...files.value, ...newFiles] : newFiles.slice(0, 1);
    const totalSize = allFiles.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > MAX_UPLOAD_SIZE) return showError(TOO_BIG_KEY);
    model.value = props.multiple ? allFiles : allFiles[0];
  }

  function remove(file: File) {
    model.value = props.multiple ? without(files.value, file) : null;
  }
</script>

<style lang="scss">
  .c-file-upload {
    border: 1px dashed $border-color;
    border-radius: $border-radius;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    padding: 3rem;

    &-unsupported-file {
      color: $danger;
    }

    &-drag-active {
      background-color: $neutral-50;
    }

    &-description {
      font-weight: $font-weight-bold;
    }

    &-selection {
      position: relative;
      color: unset;
      width: 100%;
      margin-top: .5rem;

      &-remove {
        color: $danger-700;
      }
    }
  }
</style>